import { rng } from "./main";

export interface TileType {
    name: string;
    id: number;
    img: string;
}
const imageSize = '100x100';

export const TILE_TYPES = {
    Chop: { name: "Chop Action", id: 0, img: 'https://ucarecdn.com/2a4ba713-b459-47d8-b66f-d27ffbe2e900/-/preview/' + imageSize},
    ChopGold: { name: "Chop Action with Gold", id: 1, img: 'https://ucarecdn.com/ecc2b721-5a4b-4b6f-b507-c467702ac31b/-/preview/' + imageSize},
    ChopUpgraded: { name: "Chop Upgraded Action", id: 2, img: 'https://ucarecdn.com/84f50878-4f38-4df1-9282-0b8421c9e123/-/preview/' + imageSize },
    Wood: { name: "Wood", id: 3, img: 'https://ucarecdn.com/43e4d2bb-f533-4ad1-9e7d-c0aefea51274/-/preview/' + imageSize },
    Artifact: { name: "Artifact", id: 4, img: 'https://ucarecdn.com/c86ce1d0-a567-460d-acdd-5b50d8820c30/-/preview/' + imageSize },
    Hardwood: { name: "Hardwood", id: 5, img: 'https://ucarecdn.com/d6564d8c-25cc-4edf-8879-ebf82a1a99d8/-/preview/' + imageSize },
    Bone: { name: "Bone", id: 6, img: 'https://ucarecdn.com/d81e946a-4473-47b0-bf42-4e64afe1f8ec/-/preview/' + imageSize },
    Jar: { name: "Jar", id: 7, img: 'https://ucarecdn.com/e7f30f7a-2879-4d41-be7d-04fc57165aa3/-/preview/' + imageSize },
    Mine: { name: "Mine Action", id: 8, img: 'https://ucarecdn.com/d761e7b0-2897-4fb2-a250-547b68e3ff36/-/preview/' + imageSize },
    MineUpgraded: { name: "Mine Upgraded Action", id: 9, img: 'https://ucarecdn.com/85560335-b333-43e1-9b62-2d5685a7a16b/-/preview/' + imageSize },
    MineGold: { name: "Mine Action with Gold", id: 10, img: 'https://ucarecdn.com/031e59e2-2f3f-4944-8b35-fa81ec73aefd/-/preview/' + imageSize },
    Rock: { name: "Rock", id: 11, img: 'https://ucarecdn.com/9efedd22-a3a2-4ef3-aa23-076a10b89f78/-/preview/' + imageSize },
    Copper: { name: "Copper", id: 12, img: 'https://ucarecdn.com/3064a0e6-9d17-48cb-92b8-b8f102f7044f/-/preview/' + imageSize },
    Iron: { name: "Iron", id: 13, img: 'https://ucarecdn.com/19a75df1-e714-44f2-841c-278167161e7d/-/preview/' + imageSize },
    Ruby: { name: "Ruby", id: 14, img: 'https://ucarecdn.com/9d8f5081-af24-4a65-8987-ac0af4212d14/-/preview/' + imageSize },
    Geode: { name: "Geode", id: 15, img: 'https://ucarecdn.com/feb7244f-9cad-4322-80ca-f3bc96142b3a/-/preview/' + imageSize },
    Crystal: { name: "Crystal", id: 16, img: 'https://ucarecdn.com/40046a1f-70f3-43e8-89cd-0f885d556d01/-/preview/' + imageSize },
    Rainbow: { name: "Rainbow", id: 17, img: 'https://ucarecdn.com/c3d0c9a5-0761-423a-af37-df8f2887c309/-/preview/' + imageSize },
} as const;

export type TileTypeKey = keyof typeof TILE_TYPES;

interface ResourceRowDistribution {
    Upgrades: number;
    Resources: Partial<Record<TileTypeKey, number>>;
}

const forestRowDistributions: ResourceRowDistribution[] = [
    { Upgrades: 3, Resources:       { Wood: 6} },
    { Upgrades: 2, Resources:       { Wood: 5} },
    { Upgrades: 3, Resources:       { Wood: 2, Artifact: 2, Hardwood: 2}},
    { Upgrades: 3, Resources:       { Wood: 1, Artifact: 2, Hardwood: 2}},
    { Upgrades: 3, Resources:       { Hardwood: 4, Bone: 2}},
    { Upgrades: 4, Resources:       { Hardwood: 4, Bone: 2}},
    { Upgrades: 3, Resources:       { Hardwood: 3, Geode: 1, Jar: 2}},
    { Upgrades: 4, Resources:       { Hardwood: 2, Geode: 2, Jar: 2}},
];

const mineRowDistributions: ResourceRowDistribution[] = [
    { Upgrades: 2, Resources:       { Rock: 5, Copper: 1}},
    { Upgrades: 3, Resources:       { Rock: 4, Copper: 1}},
    { Upgrades: 3, Resources:       { Copper: 4, Rock: 2}},
    { Upgrades: 3, Resources:       { Copper: 2, Iron: 3}},
    { Upgrades: 3, Resources:       { Ruby: 2, Iron: 2, Geode: 1, Artifact: 1}},
    { Upgrades: 4, Resources:       { Ruby: 2, Iron: 2, Geode: 1, Artifact: 1}},
    { Upgrades: 3, Resources:       { Bone: 1, Geode: 2, Crystal: 2, Rainbow: 1}},
    { Upgrades: 4, Resources:       { Crystal: 2, Bone: 1, Geode: 2, Rainbow: 1}},
];

interface PathStep {
    column: number;
    resource: TileTypeKey;
    upgrade: boolean;
}

type Path = PathStep[][];

const forestPath: Path = [
    [{ column: 4, resource: 'Chop', upgrade: false}],
    [{ column: 4, resource: 'Wood', upgrade: false}, { column: 3, resource: 'Chop', upgrade: false} ],
    [{ column: 3, resource: 'Wood', upgrade: false}],
    [{ column: 3, resource: 'ChopGold', upgrade: false}, { column: 2, resource: 'Wood', upgrade: false} ],
    [{ column: 2, resource: 'ChopUpgraded', upgrade: false}],
    [{ column: 2, resource: 'Hardwood', upgrade: false}],
    [{ column: 2, resource: 'ChopUpgraded', upgrade: false}],
    [{ column: 2, resource: 'Hardwood', upgrade: false}],
];

const minePath: Path = [
    [{ column: 4, resource: 'Mine', upgrade: false}],
    [{ column: 4, resource: 'Rock', upgrade: false}, { column: 3, resource: 'Mine', upgrade: false} ],
    [{ column: 3, resource: 'Copper', upgrade: false}],
    [{ column: 3, resource: 'MineGold', upgrade: false}, { column: 2, resource: 'Copper', upgrade: false} ],
    [{ column: 2, resource: 'MineUpgraded', upgrade: false}],
    [{ column: 2, resource: 'Iron', upgrade: false}],
    [{ column: 2, resource: 'MineUpgraded', upgrade: false}],
    [{ column: 2, resource: 'Geode', upgrade: false}],
];

export class Tile {
    type: TileType;
    position: { x: number, y: number };
    discovered: boolean;
    upgrade: boolean;
    requiresUpgradedTool = false;

    constructor(type: TileTypeKey, x: number, y: number, upgrade: boolean) {
        this.type = TILE_TYPES[type];
        this.position = { x, y };
        this.discovered = false;
        this.upgrade = upgrade;
        this.requiresUpgradedTool = false;
    }

    static fromSaved(value: unknown, expectedX: number, expectedY: number): Tile | null {
        if (!isRecord(value)) {
            return null;
        }

        const position = value.position;
        if (!isRecord(position) || position.x !== expectedX || position.y !== expectedY) {
            return null;
        }

        const typeKey = getTileTypeKey(value.type);
        if (typeKey == null) {
            return null;
        }

        const tile = new Tile(typeKey, expectedX, expectedY, Boolean(value.upgrade));
        tile.discovered = Boolean(value.discovered);
        tile.requiresUpgradedTool = Boolean(value.requiresUpgradedTool);
        return tile;
    }
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
}

export function getTileTypeKey(typeValue: unknown): TileTypeKey | null {
    if (!isRecord(typeValue)) {
        return null;
    }

    const id = typeValue.id;
    const name = typeValue.name;
    return (Object.keys(TILE_TYPES) as TileTypeKey[]).find((key) => {
        const tileType = TILE_TYPES[key];
        return (typeof id === 'number' && tileType.id === id) ||
            (typeof name === 'string' && tileType.name === name);
    }) ?? null;
}

function shuffleArray<T>(array: T[]): T[] {
    for (let i = array.length - 1; i > 0; i--) {
        const j = Math.floor(rng.random() * (i + 1));
        [array[i], array[j]] = [array[j], array[i]];
    }
    return array;
}

interface TileContent {
    typeKey: TileTypeKey;
    upgrade: boolean;
    requiresUpgradedTool: boolean;
}

function getTileContent(tile: Tile): TileContent | null {
    const typeKey = getTileTypeKey(tile.type);
    if (typeKey == null) {
        return null;
    }

    return {
        typeKey,
        upgrade: tile.upgrade,
        requiresUpgradedTool: tile.requiresUpgradedTool,
    };
}

function applyTileContent(tile: Tile, content: TileContent): void {
    tile.type = TILE_TYPES[content.typeKey];
    tile.upgrade = content.upgrade;
    tile.requiresUpgradedTool = content.requiresUpgradedTool;
}

function isPathTile(path: Path, x: number, y: number): boolean {
    return path[y]?.some((pathStep) => pathStep.column === x) ?? false;
}

function shuffleHiddenTiles(tileSet: Tile[][], path: Path): void {
    for (let y = 0; y < tileSet.length; y += 2) {
        const hiddenTiles: Tile[] = [];

        for (let rowOffset = 0; rowOffset < 2 && y + rowOffset < tileSet.length; rowOffset++) {
            const rowIndex = y + rowOffset;
            for (const tile of tileSet[rowIndex]) {
                if (!tile.discovered && !isPathTile(path, tile.position.x, tile.position.y)) {
                    hiddenTiles.push(tile);
                }
            }
        }

        const shuffledContents = hiddenTiles
            .map(getTileContent)
            .filter((content): content is TileContent => content != null);

        if (shuffledContents.length !== hiddenTiles.length) {
            continue;
        }

        shuffleArray(shuffledContents);
        hiddenTiles.forEach((tile, index) => {
            applyTileContent(tile, shuffledContents[index]);
        });
    }
}

export function createMap(width: number, height: number, resources: ResourceRowDistribution[], path: Path): Tile[][] {
    const grid: Tile[][] = [];

    // Iterate over rows in pairs
    for (let y = 0; y < height; y += 2) {
        let combinedResources: TileTypeKey[] = [];
        let combinedUpgrades: boolean[] = [];
        let totalUpgrades = 0;

        // Combine resources and calculate total upgrades for the two rows
        for (let rowOffset = 0; rowOffset < 2 && (y + rowOffset) < height; rowOffset++) {
            const currentRow = resources[y + rowOffset];

            // Add the resources and upgrades from both rows into combined arrays
            for (const [resource, count] of Object.entries(currentRow.Resources)) {
                for (let i = 0; i < count; i++) {
                    combinedResources.push(resource as TileTypeKey);
                }
            }

            // Track the total number of upgrades for this pair of rows
            totalUpgrades += currentRow.Upgrades;
        }

        // Create an array of booleans for upgrades, with `true` representing an upgrade
        combinedUpgrades = Array(combinedResources.length).fill(false);
        for (let i = 0; i < totalUpgrades; i++) {
            combinedUpgrades[i] = true;
        }

        // Shuffle both resources and upgrades
        combinedResources = shuffleArray(combinedResources);
        combinedUpgrades = shuffleArray(combinedUpgrades);

        let resourceIndex = 0;

        // Distribute the shuffled resources and upgrades back into the two rows
        for (let rowOffset = 0; rowOffset < 2 && (y + rowOffset) < height; rowOffset++) {
            const row: Tile[] = [];

            for (let x = 0; x < width; x++) {
                let tile = null;

                // CHECK IF THIS IS A PATH ELEMENT
                let pathRow = path[y + rowOffset];

                for (let index = 0; index < pathRow.length; index++) {
                    const pathElement = pathRow[index];

                    if (pathElement.column == x) {
                        tile = new Tile(pathElement.resource, x, y + rowOffset, pathElement.upgrade);
                    }
                }

                // If it's not a path element, assign a random tile from shuffled resources
                if (tile == null) {
                    tile = new Tile(combinedResources[resourceIndex], x, y + rowOffset, combinedUpgrades[resourceIndex]);
                    resourceIndex++;
                }

                row.push(tile);
            }

            grid.push(row);
        }
    }

    return grid;
}

export function CreateRandomMine(): Tile[][] {
    return createMap(7, 8, mineRowDistributions, minePath);
}

export function CreateRandomForest(): Tile[][] {
    return createMap(7, 8, forestRowDistributions, forestPath);
}

export function ShuffleHiddenMine(tileSet: Tile[][]): void {
    shuffleHiddenTiles(tileSet, minePath);
}

export function ShuffleHiddenForest(tileSet: Tile[][]): void {
    shuffleHiddenTiles(tileSet, forestPath);
}
